<template>
  <section
    class="bg-surface-0 dark:bg-surface-900 shadow-sm rounded-lg p-6"
    data-test-id="supplier-notes-panel"
  >
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <h3 class="font-semibold text-surface-900 dark:text-surface-0">
          Notes
        </h3>
        <Badge
          v-if="!isLoading && notesStore.noteCount > 0"
          :value="notesStore.noteCount"
          severity="secondary"
          data-test-id="supplier-notes-count"
        />
      </div>
      <Button
        v-if="canEdit && !isComposing"
        label="Add note"
        icon="pi pi-plus"
        size="small"
        outlined
        :disabled="
          isLoading || notesStore.saving || notesStore.merchantNotFound
        "
        data-test-id="supplier-notes-add-button"
        @click="startCompose"
      />
    </div>

    <!-- Compose -->
    <div v-if="isComposing" class="mb-4" data-test-id="supplier-notes-compose">
      <Textarea
        v-model="draft"
        rows="3"
        autoResize
        class="w-full"
        placeholder="Add context about this supplier"
        aria-label="New note"
        data-test-id="supplier-notes-draft-input"
      />
      <div class="flex justify-end gap-2 mt-2">
        <Button
          label="Cancel"
          size="small"
          text
          data-test-id="supplier-notes-cancel-button"
          @click="cancelCompose"
        />
        <Button
          label="Save"
          size="small"
          :loading="notesStore.saving"
          :disabled="!draft.trim() || notesStore.saving"
          data-test-id="supplier-notes-save-button"
          @click="saveNote"
        />
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="space-y-3"
      data-test-id="supplier-notes-loading"
    >
      <div v-for="i in 3" :key="i" class="space-y-1.5">
        <Skeleton width="100%" height="1rem" />
        <Skeleton width="40%" height="0.75rem" />
      </div>
    </div>

    <!-- Untracked supplier is an expected answer, not an error: no toast. -->
    <p
      v-else-if="notesStore.merchantNotFound"
      class="text-sm text-surface-600 dark:text-surface-300"
      data-test-id="supplier-notes-merchant-missing"
    >
      This supplier isn't tracked in the Fraud Command Center, so notes aren't
      available for it.
    </p>

    <p
      v-else-if="notesStore.error"
      class="text-sm text-surface-600 dark:text-surface-300"
      data-test-id="supplier-notes-error"
    >
      Couldn't load notes for this supplier.
    </p>

    <!-- The copy names the Add note button, so it is gated on the same
         permission that renders it. -->
    <p
      v-else-if="!notesStore.noteCount"
      class="text-sm text-surface-600 dark:text-surface-300"
      data-test-id="supplier-notes-empty"
    >
      <template v-if="canEdit">
        No notes yet. Use Add note to record context about this supplier.
      </template>
      <template v-else>No notes have been recorded for this supplier.</template>
    </p>

    <!-- Notes -->
    <div v-else class="space-y-3" data-test-id="supplier-notes-list">
      <article
        v-for="note in visibleNotes"
        :key="note.NOTE_ID"
        class="pb-3 border-b border-surface-200 dark:border-surface-700 last:border-b-0 last:pb-0"
        :data-test-id="`supplier-note-${note.NOTE_ID}`"
      >
        <div v-if="editingId === note.NOTE_ID">
          <Textarea
            v-model="editDraft"
            rows="3"
            autoResize
            class="w-full"
            aria-label="Edit note"
            data-test-id="supplier-notes-edit-input"
          />
          <div class="flex justify-end gap-2 mt-2">
            <Button
              label="Cancel"
              size="small"
              text
              data-test-id="supplier-notes-edit-cancel"
              @click="cancelEdit"
            />
            <Button
              label="Save"
              size="small"
              :loading="notesStore.saving"
              :disabled="!editDraft.trim() || notesStore.saving"
              data-test-id="supplier-notes-edit-save"
              @click="saveEdit(note.NOTE_ID)"
            />
          </div>
        </div>

        <template v-else>
          <!-- Header: who wrote it, then when and the write controls. -->
          <div
            class="flex items-center justify-between gap-3 min-h-8"
            :data-test-id="`supplier-note-meta-${note.NOTE_ID}`"
          >
            <div
              class="flex items-center gap-2 min-w-0 text-sm font-semibold text-surface-900 dark:text-surface-0"
            >
              <span
                v-if="authorOf(note)"
                class="truncate"
                :data-test-id="`supplier-note-author-${note.NOTE_ID}`"
              >
                {{ authorOf(note) }}
              </span>
            </div>

            <div class="flex items-center gap-1 shrink-0">
              <span class="text-xs text-surface-500 dark:text-surface-400">
                {{ formatDateTimeForDisplay(note.CREATED_AT) }}
                <template v-if="note.UPDATED_AT"> · edited</template>
              </span>
              <span v-if="canEdit" class="flex items-center gap-1">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  text
                  aria-label="Edit note"
                  :disabled="notesStore.saving"
                  :data-test-id="`supplier-note-edit-${note.NOTE_ID}`"
                  @click="startEdit(note)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  text
                  severity="danger"
                  aria-label="Delete note"
                  :disabled="notesStore.saving"
                  :data-test-id="`supplier-note-delete-${note.NOTE_ID}`"
                  @click="deleteNote(note.NOTE_ID)"
                />
              </span>
            </div>
          </div>

          <p
            class="mt-1 text-sm text-surface-800 dark:text-surface-100 whitespace-pre-wrap break-words"
          >
            {{ note.BODY }}
          </p>
        </template>
      </article>

      <Paginator
        v-if="notesStore.noteCount > PAGE_SIZE"
        v-model:first="first"
        :rows="PAGE_SIZE"
        :total-records="notesStore.noteCount"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        data-test-id="supplier-notes-paginator"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import Skeleton from 'primevue/skeleton'
import Textarea from 'primevue/textarea'
import Paginator from 'primevue/paginator'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { useFCCSupplierNotesStore } from '@/stores/fcc'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import { errorToastWithDetails } from '@/util/toasts'
import { deleteDialog } from '@/util/dialogs.js'
import { ApiError } from '@/util/ApiError'
import type { SupplierNote } from '@/types/fcc'

/** Notes per page. The full set is in memory, so paging costs no request. */
const PAGE_SIZE = 5

interface Props {
  merchantId: string | null
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), { isLoading: false })

const { authState } = useFrontegg()
const toast = useToast()
const confirm = useConfirm()
const notesStore = useFCCSupplierNotesStore()
const { hasPerms } = usePerms()

const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))

const isComposing = ref(false)
const draft = ref('')
const editingId = ref<number | null>(null)
const editDraft = ref('')
/** Index of the first note on the current page, as Paginator models it. */
const first = ref(0)

const isLoading = computed(() => props.isLoading || notesStore.loading)

const visibleNotes = computed(() =>
  notesStore.sortedNotes.slice(first.value, first.value + PAGE_SIZE),
)

// Step back when the offset outruns the list, so deleting the last note on the
// final page does not strand the reader on an empty one.
watch(
  () => notesStore.noteCount,
  count => {
    if (first.value >= count) {
      first.value = Math.max(0, (Math.ceil(count / PAGE_SIZE) - 1) * PAGE_SIZE)
    }
  },
)

// The profile swaps merchant without remounting, so the fetch follows the id
// rather than onMounted.
watch(
  () => props.merchantId,
  merchantId => {
    resetComposeState()
    if (!merchantId) {
      notesStore.reset()
      return
    }
    void notesStore.fetchNotes(authState, merchantId).catch(() => {
      // The panel renders the failure; a toast here would double-report it.
    })
  },
  { immediate: true },
)

function resetComposeState() {
  isComposing.value = false
  draft.value = ''
  editingId.value = null
  editDraft.value = ''
  first.value = 0
}

function startCompose() {
  isComposing.value = true
  draft.value = ''
}

function cancelCompose() {
  isComposing.value = false
  draft.value = ''
}

async function saveNote() {
  if (!props.merchantId || !draft.value.trim()) return
  const body = draft.value.trim()
  // Close the composer and return to page one: the note is appended
  // optimistically and sorts newest-first, so it is about to appear there.
  isComposing.value = false
  draft.value = ''
  first.value = 0
  try {
    await notesStore.addNote(
      authState,
      props.merchantId,
      body,
      authState.user?.email ?? '',
    )
  } catch (err) {
    const apiError = err as ApiError
    errorToastWithDetails(
      toast,
      'Note not saved',
      apiError.toToastMessage(),
      apiError,
    )
  }
}

/**
 * The author's email, or nothing at all: notes written before the API forwarded
 * the JWT claims have none, and the label is dropped rather than left blank.
 */
function authorOf(note: SupplierNote): string | null {
  return note.AUTHOR_EMAIL?.trim() || null
}

function startEdit(note: SupplierNote) {
  editingId.value = note.NOTE_ID
  editDraft.value = note.BODY
}

function cancelEdit() {
  editingId.value = null
  editDraft.value = ''
}

async function saveEdit(noteId: number) {
  if (!props.merchantId || !editDraft.value.trim()) return
  const body = editDraft.value.trim()
  editingId.value = null
  try {
    await notesStore.editNote(authState, props.merchantId, noteId, body)
  } catch (err) {
    const apiError = err as ApiError
    errorToastWithDetails(
      toast,
      'Note not updated',
      apiError.toToastMessage(),
      apiError,
    )
  }
}

/**
 * A delete cannot be undone from the panel, so it asks first. The note is only
 * withdrawn from the list once the operator accepts.
 */
function deleteNote(noteId: number) {
  deleteDialog(
    confirm,
    'Delete note',
    'This note will no longer appear on the supplier profile.',
    () => void removeNote(noteId),
  )
}

async function removeNote(noteId: number) {
  if (!props.merchantId) return
  try {
    await notesStore.removeNote(authState, props.merchantId, noteId)
  } catch (err) {
    const apiError = err as ApiError
    errorToastWithDetails(
      toast,
      'Note not deleted',
      apiError.toToastMessage(),
      apiError,
    )
  }
}
</script>
